import { Injectable, inject } from '@angular/core';
import { FormSchemaService } from './form-schema.service';
import { FilingFormStateService } from './filing-form-state.service';
import { CrossReferenceService } from './cross-reference.service';
import { SHEET_KEYS } from '../../models/sheet-keys';
import { humanizeQname } from './equality-message';
import { getVisibleColumnSuffixes } from './validation-field-key.util';
import { ValidationSchemaControl } from '../../models/validation-schema.model';

/**
 * Logical sheet-name aliases for the "active filing-info source".
 *
 * role0 (filingPrerequisites) and role1 (filingInfo) are the Phase 1 and
 * Phase 2 editable surfaces for the same conceptual filing-info sheet.
 * Schema files historically referenced the Phase 1 slot by name
 * ("filingPrerequisites") and a few referenced the Phase 2 slot
 * ("filingInfo"). Under PR-D's strict phase-aware hydration, only ONE slot
 * is populated per phase — so a literal lookup on the non-active slot
 * returns empty, silently breaking cross-sheet warnings.
 *
 * Both names are treated as the same logical sheet. The cross-sheet
 * resolver translates either to whichever state key is active in the
 * current phase via FilingFormStateService.getActiveFilingInfoKey().
 */
const FILING_INFO_SHEET_ALIASES: Set<string> = new Set([SHEET_KEYS.filingInfo, SHEET_KEYS.filingPrerequisites]);

/** Fallback sheet labels when the referenced sheet's schema is not yet cached */
export const SHEET_LABEL_FALLBACK: { [key: string]: string } = {
  statementOfFinancialPosition: 'Statement of Financial Position',
  subClassificationOfAssetsLiabilitiesEquity: 'Sub-classification of Assets, Liabilities & Equity',
  subClassificationLiquidity: 'Sub-classification (Liquidity Order)',
  statementOfProfitOrLoss: 'Statement of Profit or Loss',
  analysisOfProfitOrLoss: 'Analysis of Profit or Loss',
  statementOfProfitOrLossByNature: 'Statement of Profit or Loss (By Nature)',
  analysisOfProfitOrLossByNature: 'Analysis of Profit or Loss (By Nature)',
  statementOfComprehensiveIncome: 'Statement of Comprehensive Income',
  statementOfComprehensiveIncomeBeforeTax: 'Statement of Comprehensive Income (Before Tax)',
  statementOfCashFlowsIndirectMethod: 'Statement of Cash Flows (Indirect Method)',
  statementOfChangesInEquity: 'Statement of Changes in Equity',
};

/** Maps state-service sheet keys to role numbers (for FormSchemaService lookup) */
export const STATE_KEY_TO_ROLE_MAP: { [key: string]: string } = {
  statementOfFinancialPosition: '210000',
  subClassificationOfAssetsLiabilitiesEquity: '210100',
  subClassificationLiquidity: '220100',
  statementOfProfitOrLoss: '310000',
  analysisOfProfitOrLoss: '310100',
  statementOfProfitOrLossByNature: '320000',
  analysisOfProfitOrLossByNature: '320100',
  statementOfComprehensiveIncome: '410000',
  statementOfComprehensiveIncomeBeforeTax: '420000',
  statementOfCashFlowsIndirectMethod: '520000',
  statementOfChangesInEquity: '610000',
};

/**
 * DI-backed support shared by the financial validation services: schema cache,
 * label/sheet-key resolution, and column visibility. Holds no validation logic.
 */
@Injectable({ providedIn: 'root' })
export class ValidationContextService {
  private formSchemaService = inject(FormSchemaService);
  private filingFormState = inject(FilingFormStateService);
  private crossRefService = inject(CrossReferenceService);

  /** Cache schemas by state-service sheet key for label lookups in resolveFieldLabel() */
  private schemaCache: { [sheetKey: string]: any } = {};

  /**
   * Preload schemas for all sheets referenced by equality validation rules.
   * This ensures field labels are available in resolveFieldLabel() instead of raw qnames.
   *
   * @param schema - Form schema containing controls with equality validation rules
   * @param taxonomy - Current taxonomy ("MFRS" or "MPERS")
   */
  async preloadReferencedSchemas(schema: any, taxonomy: 'MFRS' | 'MPERS'): Promise<void> {
    if (!schema?.controls) return;

    // Collect unique referenced sheet keys from equality rules
    const referencedSheetKeys = new Set<string>();
    schema.controls.forEach((control: any) => {
      if (control.validation?.equality) {
        control.validation.equality.forEach((rule: any) => {
          if (rule.sheet) {
            referencedSheetKeys.add(rule.sheet);
          }
        });
      }
    });

    // Map state-service keys to role numbers and preload via FormSchemaService
    const sheetKeyToRoleNumber = new Map<string, string>();
    referencedSheetKeys.forEach(key => {
      const roleNumber = STATE_KEY_TO_ROLE_MAP[key];
      if (roleNumber) {
        sheetKeyToRoleNumber.set(key, roleNumber);
      }
    });

    const roleNumbers = Array.from(sheetKeyToRoleNumber.values());

    if (roleNumbers.length > 0) {
      await this.formSchemaService.preloadSchemas(roleNumbers, taxonomy);

      // Populate this service's schemaCache so resolveFieldLabel() can resolve field
      // labels directly without relying on FormSchemaService fallback
      for (const [sheetKey, roleNumber] of sheetKeyToRoleNumber) {
        if (!this.schemaCache[sheetKey]) {
          const loadedSchema = this.formSchemaService.getCachedSchema(roleNumber, taxonomy);
          if (loadedSchema) {
            this.schemaCache[sheetKey] = loadedSchema;
          }
        }
      }
    }
  }

  /** Read a previously cached sheet schema (by state-service sheet key). */
  getCachedSchema(sheetKey: string): any | undefined {
    return this.schemaCache[sheetKey];
  }

  /** Cache a sheet's own schema (called by EqualityValidationService at validate time). */
  cacheSchema(sheetKey: string, schema: any): void {
    this.schemaCache[sheetKey] = schema;
  }

  /**
   * Translate a schema-declared sheet name to the state-key that actually
   * holds its data for the current phase. Filing-info aliases (filingInfo /
   * filingPrerequisites) both resolve to whichever slot is active; any other
   * sheet name passes through unchanged. See FILING_INFO_SHEET_ALIASES for
   * rationale.
   */
  resolveSheetKey(sheetName: string | undefined): string | undefined {
    if (!sheetName) return sheetName;
    return FILING_INFO_SHEET_ALIASES.has(sheetName)
      ? this.filingFormState.getActiveFilingInfoKey()
      : sheetName;
  }

  /**
   * Resolve a human-readable label for a field qname.
   *
   * Resolution order:
   *   1. The cached schema for the referenced sheet (control.label).
   *   2. FormSchemaService for that sheet's role number.
   *   3. The qname's local part (after the ':') as a last resort.
   *
   * Step 3 guarantees a raw, namespace-prefixed qname (e.g.
   * "ifrs-full:ProfitLoss") never leaks into a user-facing message when the
   * referenced sheet's schema has not been cached yet — matching the safety
   * net Role 15 already applies for its ComprehensiveIncome cross-sheet check.
   *
   * @param fieldQname - Field qname to label (e.g. "ifrs-full:ProfitLoss")
   * @param sheetKey - State-service sheet key holding the field
   * @param taxonomy - Current taxonomy for FormSchemaService lookup
   * @returns Best available human-readable label
   */
  resolveFieldLabel(
    fieldQname: string,
    sheetKey: string,
    taxonomy: 'MFRS' | 'MPERS'
  ): string {
    const cachedControl = this.schemaCache[sheetKey]?.controls?.find(
      (control: ValidationSchemaControl) => control.qname === fieldQname
    );
    if (cachedControl?.label) {
      return cachedControl.label;
    }

    const roleNumber = STATE_KEY_TO_ROLE_MAP[sheetKey];
    if (roleNumber) {
      const fsLabel = this.formSchemaService.getFieldLabel(roleNumber, fieldQname, taxonomy);
      if (fsLabel && fsLabel !== fieldQname) {
        return fsLabel;
      }
    }

    return humanizeQname(fieldQname);
  }

  /** Returns visible column suffixes based on filing config */
  getVisibleColumnSuffixes(): string[] {
    return getVisibleColumnSuffixes(
      this.crossRefService.isConsolidated(),
      this.crossRefService.isSubsequentPreparation()
    );
  }

  sheetLabel(sheetKey: string): string {
    return SHEET_LABEL_FALLBACK[sheetKey] ?? sheetKey;
  }
}
